import '../styles/styles.scss';
import '../styles/globals.css';

import React, { useState } from 'react';
import { AppContext, AppProps } from 'next/app';
import { IncomingMessage, ServerResponse } from 'http';

import { NextIntlClientProvider } from 'next-intl';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// context
import { CartProvider } from 'contexts/CartContext';
import { CheckoutSessionProvider } from 'contexts/CheckoutSessionContext';

// services
import {
  getProductConfig,
  getSellerData,
  getProductDataByPlanId,
} from 'services/repositories';

// adapters
import { logger } from 'adapters/Logger';

// general configs
import {
  DEFAULT_BUSINESS_BY_PRODUCT,
  DEFAULT_PRODUCT_CODE_BY_PRODUCT,
  formatProductSlugToProductCode,
  convertUrlToFlowId,
  CHECKOUT_CONFIG_BLACKLIST_PAGES,
  SELLER_BLACKLIST_PAGES,
} from 'themes/generalConfigs';

// utils
import {
  getArea,
  getProductByDomainOrProductSlug,
  getRawQuery,
  getEnvironment,
  getUserGA,
} from 'utils/globalFunctions';
import { normalizeQueryParams } from 'utils/cartParams';
import { resolveProductFlow } from 'utils/productFlow/resolveProductFlow';
import { isSessionStorageEnabled } from 'utils/getSessionStorageStatus';

// containers
import AppRoot from 'containers/App';
import { UnleashProvider } from 'containers/UnleashProvider';
import { PosthogProvider, PosthogIdentify } from 'containers/PosthogProvider';

// components
import SessionErrorModal from 'componentsV2/SessionErrorModal';
import { ErrorBoundary } from 'componentsV2/ErrorBoundary';

// types
import type {
  GetProductDataByPlanIdResponse,
  GetProductConfigResponse,
} from 'services/repositories';

logger.init();

const redirectToErrorPage = (
  res: ServerResponse<IncomingMessage>,
  pageProps: Record<string, unknown>
) => {
  if (!res.headersSent) {
    res.writeHead(307, {
      Location: `/expired-link${pageProps?.rawQuery}`,
    });
    res.end();
  }
};

export default function MyApp({ Component, pageProps }: AppProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
          },
        },
      })
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <NextIntlClientProvider
          messages={pageProps.messages}
          locale={pageProps.locale}
        >
          <UnleashProvider>
            <PosthogProvider>
              <CartProvider pageProps={pageProps}>
                <PosthogIdentify />
                <CheckoutSessionProvider>
                  <AppRoot
                    productGTM={pageProps?.checkoutGTM}
                    productTitle={pageProps?.productTitle}
                  >
                    {isSessionStorageEnabled() ? (
                      <Component {...pageProps}></Component>
                    ) : null}
                    {!isSessionStorageEnabled() && <SessionErrorModal />}
                  </AppRoot>
                </CheckoutSessionProvider>
              </CartProvider>
            </PosthogProvider>
          </UnleashProvider>
        </NextIntlClientProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

MyApp.getInitialProps = async ({ ctx }: AppContext) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageProps = {} as any;

  pageProps.query = ctx?.query;

  const duplicatedQueryParams = normalizeQueryParams(pageProps.query);

  const queryLocale = ctx?.query?.locale as string;

  const locale =
    queryLocale ||
    (pageProps.query.flowId?.includes('peru') ? 'es-PE' : 'pt-BR');

  const messages = (await import(`../i18n/messages/${locale}.json`)).default;

  pageProps.locale = locale;
  pageProps.messages = messages;

  const isServer = !!ctx?.req;

  pageProps.pathname = ctx?.req
    ? new URL(`http://localhost${ctx?.req?.url}`)?.pathname
    : window?.location?.pathname;

  if (duplicatedQueryParams?.length > 0) {
    logger.warning(
      new Error('Duplicated query param received'),
      { flow: 'query-params', operation: 'normalize' },
      { params: duplicatedQueryParams, pathname: pageProps.pathname }
    );
  }

  if (pageProps.pathname?.includes('expired-link')) {
    pageProps.product = 'MERCURY';
    pageProps.business = 'mercury';
    pageProps.dsBrand = 'mercury';
    pageProps.dsMode = 'light';
    return { pageProps };
  }

  pageProps.host = ctx?.req
    ? ctx?.req?.headers?.domain == undefined
      ? ctx?.req?.headers?.host
      : ctx?.req?.headers?.domain
    : window?.location?.host;

  // @TODO: Verificar se podemos remover
  pageProps.rawQuery = getRawQuery(pageProps);

  // @TODO: Verificar se podemos remover
  pageProps.arrayQuery = pageProps?.query;

  // @TODO: Verificar se podemos remover
  pageProps.area = getArea(pageProps);

  const splittedHost = pageProps?.host?.toUpperCase()?.split('.') ?? [];
  const subdomain = splittedHost?.length > 0 ? splittedHost[0] : '';
  const environment = getEnvironment(subdomain);
  pageProps.environment = environment;

  pageProps.product = getProductByDomainOrProductSlug({
    host: pageProps?.host,
    query: pageProps?.query,
    path: pageProps?.pathname,
  });

  const productKey = pageProps?.product?.toLowerCase();
  const hasMappedDefaults = !!DEFAULT_PRODUCT_CODE_BY_PRODUCT[productKey];
  const fallbackConfigKey = hasMappedDefaults ? productKey : 'mercury';

  if (!hasMappedDefaults) {
    pageProps.product = 'MERCURY';
  }

  pageProps.query.seller = pageProps?.query?.seller ?? 'performance';
  pageProps.query.source = pageProps?.query?.source ?? 'direto';

  // @TODO: Verificar se ainda usamos esse valor
  pageProps._ga = getUserGA({
    ctx,
  });

  // @TODO: Verificar se ainda usamos esse valor
  pageProps.contactId =
    pageProps?.query?.route === 'vip' && pageProps?.query?.contactId
      ? pageProps?.query?.contactId
      : undefined;

  // @TODO: Verificar se ainda é necessário
  const updatedRawQuery = convertUrlToFlowId(pageProps);
  pageProps.rawQuery = updatedRawQuery;

  const isPathnameAvailableToGetSeller = !SELLER_BLACKLIST_PAGES.includes(
    pageProps?.pathname
  );
  const shouldCallSellerRequest =
    pageProps?.query?.seller !== 'performance' &&
    isPathnameAvailableToGetSeller &&
    pageProps.query?.source !== 'direto';

  const defaultProductCode =
    DEFAULT_PRODUCT_CODE_BY_PRODUCT[fallbackConfigKey] || 'ms';
  const defaultBusiness =
    DEFAULT_BUSINESS_BY_PRODUCT[fallbackConfigKey] || 'meusucesso';

  const { productCode, business } = formatProductSlugToProductCode({
    productSlug: pageProps?.query['product-slug'] || defaultProductCode,
    product: pageProps?.product,
    business: defaultBusiness,
  });

  pageProps.business = business;
  pageProps.productCode = productCode;

  const isPathnameAvailableToGetConfig = !CHECKOUT_CONFIG_BLACKLIST_PAGES.some(
    (page) => {
      if (page.includes('[')) {
        return pageProps.pathname?.startsWith(page.split('[')[0]);
      }
      return pageProps.pathname === page;
    }
  );

  /**
   * @description
   * Em ambiente local o hot reload afeta a aplicação pois não consegue realizar uma
   * nova consulta da config ao bff. Sendo assim, fizemos essa validação para garantir
   * que não sejamos afetados durante o desenvolvimento
   */
  const isLocalhost = pageProps.host?.includes('localhost') ?? false;

  if ((isServer || isLocalhost) && isPathnameAvailableToGetConfig) {
    const queryVoucher = pageProps.query.voucher;
    const hasQueryVoucher = !!queryVoucher;

    const planIdToResolveProduct =
      pageProps.query?.oferta || pageProps.query?.planId;
    let planIdForConfig = planIdToResolveProduct;

    let productDataByPlanId: GetProductDataByPlanIdResponse | null = null;
    /**
     * Caso exista a query param `oferta` (que é um planId), precisamos
     * buscar os dados do produto desse planId.
     */
    if (planIdToResolveProduct) {
      try {
        productDataByPlanId = await getProductDataByPlanId(
          planIdToResolveProduct,
          isServer
        );
        // FIN-8016: disponibilidade da oferta para o desvio depois do lead.
        pageProps.offerAvailability = productDataByPlanId?.plan ?? null;
      } catch (error) {
        const response = (
          error as {
            response?: { status?: number; data?: { messageCode?: string } };
          }
        )?.response;
        const isUnknownPlan =
          response?.status === 422 &&
          response?.data?.messageCode === 'PLAN_NOT_FOUND';

        if (!isUnknownPlan) {
          console.error(JSON.stringify(error));

          pageProps.checkoutGTM = null;
          pageProps.productFlow = null;
          pageProps.designSystemId = null;

          redirectToErrorPage(ctx.res, pageProps);
          return { pageProps };
        }

        logger.warning(
          new Error('Unknown planId received'),
          { flow: 'product-resolution', operation: 'planLookup' },
          { planId: planIdToResolveProduct, pathname: pageProps.pathname }
        );

        planIdForConfig = undefined;
      }
    }

    let configData: GetProductConfigResponse | null = null;
    const productCodeToGetConfig = productDataByPlanId?.code || productCode;
    try {
      if (productCodeToGetConfig) {
        pageProps.productCode = productCodeToGetConfig;

        const businessForConfig = productDataByPlanId?.business || business;
        const configParams = {
          code: productCodeToGetConfig,
          isServer,
          business: businessForConfig,
        };

        try {
          configData = await getProductConfig({
            ...configParams,
            planId: planIdForConfig,
          });
        } catch (error) {
          const upstreamResponse = (error as { response?: unknown })?.response;

          if (!planIdForConfig || !upstreamResponse) {
            throw error;
          }

          configData = await getProductConfig(configParams);
        }
      }
    } catch (error) {
      console.error(JSON.stringify(error));

      pageProps.checkoutGTM = null;
      pageProps.productFlow = null;
      pageProps.designSystemId = null;
      redirectToErrorPage(ctx.res, pageProps);
      return { pageProps };
    }

    if (!configData) {
      redirectToErrorPage(ctx.res, pageProps);
      return { pageProps };
    }

    const checkoutFlow = configData.checkoutFlow;

    /**
     * @description
     * Alguns fluxos como pagamento de fatura avulsa não possuem flow
     * mas precisam ter acesso à config no SF para montar algumas coisas como
     * o design system que será usado, então precisamos verificar se existe flow
     * quando buscamos a config
     */

    const productFlow = resolveProductFlow({
      checkoutFlow,
      query: pageProps.query,
    });

    if (productFlow?.steps?.[0]?.props?.plan?.business) {
      pageProps.business = productFlow?.steps[0].props.plan.business;
    }

    /**
     * @description
     * O Id que é salvo no Salesforce vem no formato:
     * brand$mode, então esperamos esse formato nessa constante
     */
    const [dsBrand, dsMode] = (configData.designSystemId ?? '').split('$');

    pageProps.checkoutGTM = configData.gtm;
    pageProps.productTitle = configData.title;
    pageProps.productFlow = productFlow;
    pageProps.allCheckoutFlowsByProductCode = checkoutFlow?.flows;
    pageProps.dsBrand = dsBrand || 'mercury';
    pageProps.dsMode = dsMode || 'light';
    pageProps.hasAddressForm = configData.hasAddressForm;

    /**
     * @description
     * Setamos o voucher pelo server side para prevenir certos comportamentos indesejados
     * como perder o parametro do voucher por conta do history do navegador
     */
    if (productFlow) {
      const voucherFlow = productFlow?.steps[0].props.voucher;

      if (!hasQueryVoucher && !!voucherFlow) {
        pageProps.query.voucher = voucherFlow;
        pageProps.rawQuery += `&voucher=${voucherFlow}`;
      }
    }
  }

  if (isServer && shouldCallSellerRequest) {
    const operation = 'BR';

    // @TODO: Verificar se ainda precisamos fazer essa quebra
    const splittedSource = pageProps?.query?.source?.split('-');
    const source = splittedSource
      ? splittedSource[splittedSource?.length - 1]
      : 'direto';

    try {
      const sellerData = await getSellerData(
        {
          source,
          seller: pageProps?.query?.seller,
          operation,
          product:
            DEFAULT_BUSINESS_BY_PRODUCT[pageProps?.product?.toLowerCase()],
        },
        true
      );

      pageProps.sellerName = sellerData?.name;
      pageProps.isLiveEnabled = sellerData?.isLiveEnabled;
      pageProps.sellerAccount = sellerData.account;
    } catch (err) {
      redirectToErrorPage(ctx.res, pageProps);
      return { pageProps };
    }
  }

  return { pageProps };
};
